import { useState, useRef, useCallback } from 'react'
import { TrashIcon, PhotoIcon, ArrowUpTrayIcon, StarIcon, PlusIcon } from '@heroicons/react/24/outline'
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid'
import { motion, AnimatePresence } from 'framer-motion'

function UploadZone({ onFiles, multiple = false, label, helpText, compact = false, children }) {
  const [isDragging, setIsDragging] = useState(false)
  const inputRef = useRef(null)

  const handleDrag = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDragIn = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true)
    }
  }, [])

  const handleDragOut = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      onFiles(multiple ? Array.from(files) : [files[0]])
    }
  }, [onFiles, multiple])

  return (
    <div
      onDragEnter={handleDragIn}
      onDragLeave={handleDragOut}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      className={`
        relative cursor-pointer rounded-xl border-2 border-dashed
        transition-all duration-250 group
        ${isDragging
          ? 'border-primary-500 bg-primary-50/60 dark:bg-primary-50/20 scale-[1.01] shadow-sm'
          : 'border-surface-300 dark:border-surface-300 hover:border-primary-400 dark:hover:border-primary-400 hover:bg-primary-50/30 dark:hover:bg-primary-50/10'
        }
      `}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        onChange={(e) => {
          const files = e.target.files
          if (files && files.length > 0) {
            onFiles(multiple ? Array.from(files) : [files[0]])
          }
          e.target.value = ''
        }}
        className="sr-only"
      />
      {children || (
        <div className={`flex flex-col items-center justify-center px-4 ${compact ? 'py-5' : 'py-8'}`}>
          <div className={`
            rounded-full mb-3 transition-all duration-250
            ${compact ? 'p-2.5' : 'p-3'}
            ${isDragging
              ? 'bg-primary-100 dark:bg-primary-100 scale-110'
              : 'bg-surface-100 dark:bg-surface-200 group-hover:bg-primary-50 dark:group-hover:bg-primary-100'
            }
          `}>
            {compact ? (
              <PlusIcon className={`
                h-5 w-5 transition-colors duration-200
                ${isDragging
                  ? 'text-primary-600 dark:text-primary-500'
                  : 'text-text-muted group-hover:text-primary-500'
                }
              `} />
            ) : (
              <ArrowUpTrayIcon className={`
                h-6 w-6 transition-colors duration-200
                ${isDragging
                  ? 'text-primary-600 dark:text-primary-500'
                  : 'text-text-muted group-hover:text-primary-500'
                }
              `} />
            )}
          </div>
          <p className={`font-medium text-text ${compact ? 'text-sm' : 'text-sm'} mb-0.5`}>
            {label || 'Upload photos'}
          </p>
          <p className="text-xs text-text-muted">
            {helpText || 'Drag & drop or tap to browse'}
          </p>
          {!compact && (
            <p className="text-xs text-text-muted/70 mt-0.5">JPG, PNG, WebP up to 5MB</p>
          )}
        </div>
      )}
      {/* Drag overlay pulse */}
      {isDragging && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute inset-0 rounded-xl bg-primary-500/5 dark:bg-primary-500/10 pointer-events-none"
        />
      )}
    </div>
  )
}

function ImageThumbnail({ src, alt, onRemove, isPrimary, className = '' }) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.92 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className={`relative group rounded-xl overflow-hidden
        border border-border bg-bg-secondary
        shadow-xs hover:shadow-md
        transition-shadow duration-200 ${className}`}
    >
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover"
        loading="lazy"
      />
      {/* Gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent
        opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
      {/* Primary badge */}
      {isPrimary && (
        <div className="absolute top-2 left-2 flex items-center gap-1 rounded-full
          bg-accent-500 px-2.5 py-1 text-[11px] font-bold text-white
          shadow-md backdrop-blur-sm">
          <StarIconSolid className="h-3 w-3" />
          Cover
        </div>
      )}
      {/* Remove button — always visible on mobile (no hover), hover on desktop */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onRemove()
        }}
        className="absolute top-2 right-2 p-2 rounded-full
          bg-red-500/90 text-white shadow-md backdrop-blur-sm
          sm:opacity-0 sm:group-hover:opacity-100
          hover:bg-red-600 active:scale-90
          transition-all duration-200
          min-h-[36px] min-w-[36px] flex items-center justify-center"
        aria-label="Remove image"
      >
        <TrashIcon className="w-4 h-4" />
      </button>
    </motion.div>
  )
}

export default function ImageManager({
  existingImages = [],
  primaryPhotoUrl = null,
  onPrimaryPhotoChange,
  onPhotosChange,
  onRemoveExistingImage,
  onRemovePrimaryPhoto,
  className = ''
}) {
  const [newPhotoPreviews, setNewPhotoPreviews] = useState([])
  const [newPrimaryPreview, setNewPrimaryPreview] = useState(null)

  const handlePrimaryFiles = (files) => {
    const file = files[0]
    if (!file) return
    const previewUrl = URL.createObjectURL(file)
    setNewPrimaryPreview(previewUrl)
    onPrimaryPhotoChange?.(file)
  }

  const handleAdditionalFiles = (files) => {
    if (!files || files.length === 0) return

    const newPreviews = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }))

    const updatedPreviews = [...newPhotoPreviews, ...newPreviews]
    setNewPhotoPreviews(updatedPreviews)

    const allFiles = updatedPreviews.map(p => p.file)
    onPhotosChange?.(allFiles)
  }

  const removeNewPhoto = (index) => {
    setNewPhotoPreviews(prev => {
      const updated = prev.filter((_, i) => i !== index)
      URL.revokeObjectURL(prev[index].preview)
      const remainingFiles = updated.map(p => p.file)
      onPhotosChange?.(remainingFiles)
      return updated
    })
  }

  const removePrimaryPhoto = () => {
    if (newPrimaryPreview) {
      URL.revokeObjectURL(newPrimaryPreview)
      setNewPrimaryPreview(null)
    }
    onRemovePrimaryPhoto?.()
  }

  const removeExistingImage = (imageUrl, imageId) => {
    onRemoveExistingImage?.(imageUrl, imageId)
  }

  const hasPrimary = !!(primaryPhotoUrl || newPrimaryPreview)
  const hasAdditional = existingImages.length > 0 || newPhotoPreviews.length > 0

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Primary Photo */}
      <div className="space-y-2.5">
        <div className="flex items-baseline justify-between">
          <label className="block text-sm font-semibold text-text">
            Cover Photo
          </label>
          <span className="text-xs text-text-muted">Optional</span>
        </div>
        {hasPrimary ? (
          <ImageThumbnail
            src={newPrimaryPreview || primaryPhotoUrl}
            alt="Cover photo"
            onRemove={removePrimaryPhoto}
            isPrimary
            className="aspect-[16/9] max-h-56"
          />
        ) : (
          <UploadZone
            onFiles={handlePrimaryFiles}
            label="Add a cover photo"
            helpText="This will be the first image people see"
          />
        )}
      </div>

      {/* Additional Photos */}
      <div className="space-y-3">
        <div className="flex items-baseline justify-between">
          <label className="block text-sm font-semibold text-text">
            Additional Photos
          </label>
          <span className="text-xs text-text-muted">Optional</span>
        </div>

        {/* Image Grid */}
        {hasAdditional && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <AnimatePresence mode="popLayout">
              {existingImages.map((imageUrl, index) => (
                <ImageThumbnail
                  key={`existing-${index}`}
                  src={imageUrl}
                  alt={`Photo ${index + 1}`}
                  onRemove={() => removeExistingImage(imageUrl, index)}
                  className="aspect-square"
                />
              ))}
              {newPhotoPreviews.map((item, index) => (
                <ImageThumbnail
                  key={`new-${index}`}
                  src={item.preview}
                  alt={`New photo ${index + 1}`}
                  onRemove={() => removeNewPhoto(index)}
                  className="aspect-square"
                />
              ))}
            </AnimatePresence>
          </div>
        )}

        <UploadZone
          onFiles={handleAdditionalFiles}
          multiple
          compact={hasAdditional}
          label={hasAdditional ? 'Add more photos' : 'Upload additional photos'}
          helpText={hasAdditional ? 'Tap to add more' : 'You can upload multiple images at once'}
        />
      </div>
    </div>
  )
}
